import React, { useEffect, useMemo, useState } from 'react';
import { FaBell, FaCheckCircle, FaCircle, FaSyncAlt } from 'react-icons/fa';
import { notificationAPI } from '../../utils/api';

function timeAgo(value) {
  if (!value) return '';
  const diff = Date.now() - new Date(value).getTime();
  const minutes = Math.max(1, Math.floor(diff / 60000));
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function NotificationCenterPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const unreadCount = useMemo(() => items.filter(item => item.unread).length, [items]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await notificationAPI.getNotifications({ limit: 100 });
      setItems(res.data?.content || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const markRead = async id => {
    setItems(prev => prev.map(item => item.id === id ? { ...item, unread: false } : item));
    await notificationAPI.markRead(id).catch(() => {});
  };

  const markAllRead = async () => {
    setItems(prev => prev.map(item => ({ ...item, unread: false })));
    await notificationAPI.markAllRead().catch(() => {});
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h2 className="mb-1">Notifications</h2>
          <p className="text-muted mb-0">Operational alerts from tenders, work orders, deliveries, QC, handovers, and finance.</p>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={load} disabled={loading}>
            <FaSyncAlt className="me-2" /> Refresh
          </button>
          <button className="btn btn-success btn-sm" onClick={markAllRead} disabled={unreadCount === 0}>
            <FaCheckCircle className="me-2" /> Mark all read
          </button>
        </div>
      </div>

      <div className="card shadow-sm border-0">
        <div className="card-header bg-white d-flex align-items-center justify-content-between">
          <span className="fw-semibold"><FaBell className="me-2" /> Inbox</span>
          <span className="badge bg-success">{unreadCount} unread</span>
        </div>
        <div className="list-group list-group-flush">
          {items.length === 0 && (
            <div className="list-group-item py-5 text-center text-muted">
              {loading ? 'Loading notifications...' : 'No notifications yet.'}
            </div>
          )}
          {items.map(item => (
            <button
              key={item.id}
              className={`list-group-item list-group-item-action d-flex align-items-start gap-3 py-3 ${item.unread ? 'bg-light' : ''}`}
              onClick={() => markRead(item.id)}
            >
              <FaCircle className={`mt-1 text-${item.severity === 'warning' ? 'warning' : item.severity === 'success' ? 'success' : 'primary'}`} style={{ fontSize: 10 }} />
              <span className="flex-grow-1 text-start">
                <span className="d-flex align-items-center justify-content-between gap-3">
                  <strong>{item.title}</strong>
                  <small className="text-muted">{timeAgo(item.created_at)}</small>
                </span>
                <span className="d-block text-muted mt-1">{item.message}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default NotificationCenterPage;
